import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ReactMarkdown from 'react-markdown';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  label: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  textarea: {
    width: '100%',
    minHeight: 200,
    padding: theme.spacing(1.5),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    color: theme.palette.text.primary,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    resize: 'vertical',
    outline: 'none',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
    '&:disabled': {
      opacity: 0.5,
    },
  },
  preview: {
    minHeight: 200,
    padding: theme.spacing(1.5),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    overflowY: 'auto',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    '& h1, & h2, & h3': {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(0.5),
    },
    '& h1': { fontSize: '1.25rem' },
    '& h2': { fontSize: '1.1rem' },
    '& h3': { fontSize: '1rem' },
    '& p': {
      margin: `${theme.spacing(0.5)}px 0`,
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(3),
      margin: `${theme.spacing(0.5)}px 0`,
    },
    '& li': {
      marginBottom: theme.spacing(0.25),
    },
    '& code': {
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '1px 4px',
      borderRadius: 3,
      fontSize: '0.85em',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: 'rgba(0,0,0,0.2)',
      padding: theme.spacing(1.5),
      borderRadius: 4,
      overflow: 'auto',
      margin: `${theme.spacing(1)}px 0`,
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
    '& a': {
      color: theme.palette.primary.light,
      textDecoration: 'underline',
    },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.divider}`,
      paddingLeft: theme.spacing(1.5),
      margin: `${theme.spacing(1)}px 0`,
      color: theme.palette.text.secondary,
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: `${theme.spacing(1.5)}px 0`,
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: `${theme.spacing(1)}px 0`,
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: 'rgba(255,255,255,0.05)',
      fontWeight: 600,
    },
  },
  hint: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    color: theme.palette.text.disabled,
  },
  modeButton: {
    textTransform: 'none',
    padding: '2px 10px',
    fontSize: '0.75rem',
  },
  emptyPreview: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
}));

interface MarkdownNotesInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}

const MarkdownNotesInput: React.FC<MarkdownNotesInputProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Notes',
  placeholder = 'Supports markdown: **bold**, *italic*, # headings, - lists, [links](url), `code`',
}) => {
  const classes = useStyles();
  const [mode, setMode] = useState<'edit' | 'preview'>(value ? 'preview' : 'edit');

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.label}>{label}</Typography>
        <ButtonGroup size="small" variant="outlined">
          <Button
            className={classes.modeButton}
            onClick={() => setMode('edit')}
            color={mode === 'edit' ? 'primary' : 'default'}
            variant={mode === 'edit' ? 'contained' : 'outlined'}
            startIcon={<EditIcon style={{ fontSize: 14 }} />}
          >
            Edit
          </Button>
          <Button
            className={classes.modeButton}
            onClick={() => setMode('preview')}
            color={mode === 'preview' ? 'primary' : 'default'}
            variant={mode === 'preview' ? 'contained' : 'outlined'}
            startIcon={<VisibilityIcon style={{ fontSize: 14 }} />}
          >
            Preview
          </Button>
        </ButtonGroup>
      </Box>

      {mode === 'edit' ? (
        <>
          <textarea
            className={classes.textarea}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
          />
          <Typography className={classes.hint}>Supports markdown formatting</Typography>
        </>
      ) : (
        <div className={classes.preview}>
          {value ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <Typography className={classes.emptyPreview}>No notes yet</Typography>
          )}
        </div>
      )}
    </Box>
  );
};

export default MarkdownNotesInput;
